/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { Component } from 'react';
import { css, StyleSheet } from 'aphrodite';
import { Field } from 'redux-form';

import { InputConfig } from '../../TaskInputs/types';
import { InputContainer } from '../../TaskInputs/InputContainer';
import { TaskInput } from '../../TaskInputs/TaskInput/TaskInput';
import { CountryName, AddressInput } from './types';
import { inputDefsMap } from './addressInputFieldDefinitions';
import { supportUSAndCanadaAddressInputs } from './config';

import { countryNames, addressFields } from './constants';
import { addressUtils } from './utils';
import { each, compact } from '@utils/lodash';

const styles = StyleSheet.create({
  addressInputFields: {
    display: 'inline-block',
  },
  inputWrapper: {
    display: 'inline-block',
    alignItems: 'baseline',
  },
  stateZipWrapper: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 26,
    marginBottom: 26,
  },
  stateZipElementWrapper: {
    width: '48%',
  },
  stateZipElement: {
    margin: 0,
  },
});

export interface AddressInputFormValuesShape {
  countryName?: CountryName;
}

export interface AddressInputFieldsOwnProps {
  withCounty: boolean;
  locale?: AddressInput.Locale;
}

export interface AddressInputFieldsStateProps {
  addressInputCountry: CountryName;
}

export interface AddressInputDispatchProps {
  clearAddressField: (field: string) => void;
}

type AddressInputFieldsProps = AddressInputDispatchProps & AddressInputFieldsOwnProps & AddressInputFieldsStateProps;

class AddressInputFields extends Component<AddressInputFieldsProps> {
  onCountryDropdownChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { addressInputCountry, clearAddressField, locale } = this.props;
    const newCountryValue = event.target.value as CountryName;
    const prevCountry = addressInputCountry;

    const nextCountry = addressUtils.getAddressInputType({
      locale,
      supportUSAndCanadaAddressInputs,
      countryName: newCountryValue,
    });

    if (prevCountry === countryNames.UNITED_STATES && nextCountry === countryNames.CANADA) {
      each(addressFields.US_ONLY_FIELDS, usOnlyField => clearAddressField(usOnlyField));
    }

    if (prevCountry === countryNames.CANADA && nextCountry === countryNames.UNITED_STATES) {
      each(addressFields.CANADA_ONLY_FIELDS, canadaOnlyField => clearAddressField(canadaOnlyField));
    }
  };

  render() {
    const { addressInputCountry, withCounty } = this.props;

    const fullWidthUSFields = compact([
      inputDefsMap.line1,
      inputDefsMap.line2,
      inputDefsMap.city,
      withCounty && inputDefsMap.county,
    ]);

    const inlineUSFields = [inputDefsMap.stateCode, inputDefsMap.postalCode];

    const fullWidthCanadaFields = [inputDefsMap.line1, inputDefsMap.line2, inputDefsMap.city];

    // TODO: add Canadian fields once we know them
    const inlineCanadaFields = [] as InputConfig[];

    const getCountryDropdown = () => (
      <Field {...inputDefsMap.countryName} component={InputContainer} onChange={this.onCountryDropdownChange} />
    );

    const isCanada = addressInputCountry === countryNames.CANADA;

    return (
      <div className={css(styles.addressInputFields)}>
        {(isCanada ? fullWidthCanadaFields : fullWidthUSFields).map((input, i) => (
          <TaskInput key={i} config={input} />
        ))}
        <div className={css(styles.stateZipWrapper)}>
          {(isCanada ? inlineCanadaFields : inlineUSFields).map((input, i) => {
            // We have an ID on this element for a11y reasons; without it, the screenreader
            // reads the labels of these two elements first, then goes on to the inputs
            // themselves.
            return (
              <div className={css(styles.stateZipElementWrapper)} id={`stateZipWrapper-${i}`} key={input.id}>
                <TaskInput config={input} customStyle={styles.stateZipElement} />
              </div>
            );
          })}
        </div>
        {supportUSAndCanadaAddressInputs && <div className={css(styles.inputWrapper)}>{getCountryDropdown()}</div>}
      </div>
    );
  }
}

export default AddressInputFields;
